import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { Mail } from 'lucide-react'
import { useTopics, useRegions } from '../hooks/useTaxonomy'
import wLogoClean from '../assets/w_logo_clean.svg'
import wLogoDark from '../assets/w_logo_dark.svg'

function Footer() {
  const { data: topics = [] } = useTopics()
  const { data: regions = [] } = useRegions()

  return (
    <footer className="bg-canvas border-t border-line-strong">
      {/* Taxonomy columns */}
      <div className="hidden lg:block border-b border-line px-8 py-4">
        <div className="max-w-[1400px] mx-auto flex justify-center gap-64">
          <TaxonomyColumn
            heading="Topics"
            items={topics}
            basePath="/wire/topics"
          />
          <TaxonomyColumn
            heading="Regions"
            items={regions}
            basePath="/wire/regions"
          />
          <div className="flex flex-col items-start gap-1.5">
            <span className="text-[10px] font-semibold tracking-widest text-fg-secondary uppercase mb-1">
              Quick Links
            </span>
            <Link to="/wire/me"     className="text-xs text-fg-secondary hover:text-fg transition-colors">My Wire</Link>
            <Link to="/wire/search" className="text-xs text-fg-secondary hover:text-fg transition-colors">AI Search</Link>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="max-w-[1400px] mx-auto px-8 py-5 flex items-center justify-between gap-6">
        <Link to="/wire" className="shrink-0" aria-label="Wire home">
          <img src={wLogoClean} alt="Wire Logo" className="h-5 w-auto dark:hidden" />
          <img src={wLogoDark} alt="Wire Logo" className="hidden h-5 w-auto dark:block" />
        </Link>

        <p className="font-serif text-[11px] text-fg-secondary leading-relaxed tracking-wide text-center flex-1 px-8">
          WIRe provides analysis to a wide group of interested parties. We exemplify collaboration
          across many places to deliver timely, high quality content to customers.
          <br />
          These partnerships help navigate uncertainty and crises while maintaining our independent mission.
        </p>

        <button
          onClick={() => toast.info('Coming soon')}
          className="flex items-center gap-1.5 text-xs text-fg-secondary hover:text-fg transition-colors shrink-0"
        >
          <Mail size={12} aria-hidden="true" />
          Contact the WIRe Team
        </button>
      </div>
    </footer>
  )
}

interface TaxonomyColumnProps {
  heading: string
  items: { slug: string; label: string }[]
  basePath: string
}

function TaxonomyColumn({ heading, items, basePath }: TaxonomyColumnProps) {
  const sorted = [...items].sort((a, b) => a.label.localeCompare(b.label))
  const mid = Math.ceil(sorted.length / 2)
  const left = sorted.slice(0, mid)
  const right = sorted.slice(mid)
  return (
    <div className="flex flex-col items-center gap-2">
      <span className="text-[10px] font-semibold tracking-widest text-fg-secondary uppercase">
        {heading}
      </span>
      <div className="flex gap-8">
        <div className="flex flex-col gap-1.5">
          {left.map((item) => (
            <Link
              key={item.slug}
              to={`${basePath}/${item.slug}`}
              className="text-xs text-fg-secondary hover:text-fg transition-colors"
            >
              {item.label}
            </Link>
          ))}
        </div>
        <div className="flex flex-col gap-1.5">
          {right.map((item) => (
            <Link
              key={item.slug}
              to={`${basePath}/${item.slug}`}
              className="text-xs text-fg-secondary hover:text-fg transition-colors"
            >
              {item.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  )
}

export default Footer
